import type { CanvasChatMessage } from './canvas-types';

export type CanvasProgressInput = Pick<CanvasChatMessage, 'playbookProgress' | 'agentTaskGroups' | 'conceptsRun' | 'webResearchStatus' | 'conceptDraftProgress'>;

// Render fixed work descriptions and completed public concept titles only.
// Never render provider reasoning, internal prompts, or raw tool messages.
const STAGES: Record<string, string> = {
  clone_study: 'Reviewing the source ad.', clone_generate: 'Generating ad creatives.', clone_qa: 'Checking creative quality.', clone_retry: 'Revising clone creatives.',
  angle_context: 'Reviewing brand context.', angle_writer: 'Writing ad concepts.', angle_saver: 'Validating and saving ad concepts.',
  brand_context: 'Reviewing brand context.', own_brand_analysis: 'Reviewing brand context.',
  competitive_research: 'Researching competitors.', campaign_brief: 'Preparing the campaign brief.',
  hero_generation: 'Generating ad creatives.', ad_generation: 'Generating ad creatives.',
  qa_evaluation: 'Checking creative quality.', user_approval: 'Waiting for your approval.',
  platform_transform: 'Adapting creatives for placements.', source_analysis: 'Reviewing the source ad.',
  competitor_clone_batch: 'Generating ad creatives.', campaign_v3_clone_batch: 'Generating ad creatives.',
  brand_onboarding: 'Preparing brand context.', edit_generation: 'Applying creative edits.',
};
const count = (value: number) => Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;

export function canvasWorkingLabels(input: CanvasProgressInput): string[] {
  if (input.conceptDraftProgress && input.conceptDraftProgress.stage !== 'saved' && input.conceptsRun?.status !== 'failed' && input.conceptsRun?.status !== 'interrupted') {
    const draft = input.conceptDraftProgress;
    return [draft.stage === 'validating' ? `Validating concepts · ${count(draft.kept)} accepted` : `Drafting concepts · ${count(draft.drafted)} of ${count(draft.target)}`];
  }
  const stage = summarizeCanvasProgress(input, true)[0];
  if (stage === 'Waiting for your approval.') return ['Waiting for approval'];
  const labels: Record<string, string> = {
    'Reviewing the source ad.': 'Studying source',
    'Revising clone creatives.': 'Revising clones',
    'Writing ad concepts.': 'Writing concepts',
    'Validating and saving ad concepts.': 'Saving concepts',
    'Generating ad concepts.': 'Preparing concepts',
    'Generating ad creatives.': 'Generating creatives',
    'Reviewing brand context.': 'Reviewing brand context',
    'Researching competitors.': 'Researching',
    'Preparing the campaign brief.': 'Preparing the brief',
    'Checking creative quality.': 'Checking quality',
    'Applying creative edits.': 'Applying edits',
    'Searching for research sources.': 'Researching',
    'Reviewing research sources.': 'Reviewing sources',
    'Adapting creatives for placements.': 'Adapting creatives',
  };
  return Object.hasOwn(labels, stage) ? [labels[stage], 'Working', 'Processing'] : ['Working', 'Processing'];
}

/** A rolling snapshot of observable work, never an accumulated reasoning feed. */
export function summarizeCanvasProgress(input: CanvasProgressInput, live: boolean): string[] {
  const p = input.playbookProgress;
  const groups = input.agentTaskGroups ?? [];
  const steps = groups.flatMap(group => group.steps);
  const statuses = p ? Object.values(p.phaseStatuses) : [];
  const failed = statuses.includes('failed') || groups.some(group => group.status === 'failed') || steps.some(step => step.status === 'error') || input.conceptsRun?.status === 'failed';
  const finished = p?.isDone || input.conceptsRun?.status === 'ready' || (steps.length > 0 && steps.every(step => step.status === 'success')) || input.webResearchStatus === 'complete';
  const phase = p?.phases.find(phase => phase.id === p.activePhaseId && p.phaseStatuses[phase.id] === 'running');
  const runningTool = steps.find(step => step.status === 'running');
  let stage = live ? 'Working on the current stage.' : 'This request is no longer running.';
  if (failed) stage = 'Some work could not finish.';
  else if (input.conceptsRun?.status === 'interrupted') stage = 'Concept generation was interrupted.';
  else if (input.conceptsRun?.status === 'ready') stage = 'Ad concepts are ready.';
  else if (!live && finished) stage = 'Recorded work is complete.';
  else if (live) {
    if (phase) stage = Object.hasOwn(STAGES, phase.phaseType) ? STAGES[phase.phaseType] : stage;
    else if (runningTool) stage = Object.hasOwn(STAGES, runningTool.toolName) ? STAGES[runningTool.toolName] : stage;
    else if (input.conceptsRun?.status === 'generating') stage = 'Generating ad concepts.';
    else if (input.webResearchStatus === 'searching') stage = 'Searching for research sources.';
    else if (input.webResearchStatus === 'analyzing') stage = 'Reviewing research sources.';
  }
  let detail = live ? 'Waiting for progress updates.' : 'No recorded progress details.';
  if (statuses.length) detail = `${statuses.filter(status => status === 'completed').length} of ${statuses.length} recorded stages completed.`;
  else if (steps.length) detail = `${steps.filter(step => step.status === 'success').length} of ${steps.length} recorded steps completed.`;
  else if (input.conceptsRun) detail = `Requested concepts: ${count(input.conceptsRun.count)}.`;
  const cloneSteps = steps.filter(step => step.toolName?.startsWith('clone_') && step.toolName !== 'clone_study');
  if (cloneSteps.length) {
    const delivered = cloneSteps.filter(step => step.status === 'success').length;
    const rejected = cloneSteps.filter(step => step.status === 'error').length;
    detail = `${delivered} of ${cloneSteps.length} clones delivered${rejected ? `; ${rejected} could not finish` : ''}.`;
  }
  const draft = input.conceptDraftProgress;
  if (draft) {
    if (live && !failed && input.conceptsRun?.status !== 'interrupted' && input.conceptsRun?.status !== 'ready') {
      stage = draft.stage === 'saved' ? 'Ad concepts are saved.' : draft.stage === 'validating' ? 'Validating ad concepts.' : 'Writing ad concepts.';
    }
    detail = draft.stage === 'saved'
      ? `${count(draft.kept)} of ${count(draft.target)} concepts saved.`
      : `${count(draft.drafted)} of ${count(draft.target)} concepts drafted · ${count(draft.kept)} validated.`;
  }
  const lines = [stage, detail];
  cloneSteps.forEach((step, index) => {
    const status = step.status === 'success' ? 'Delivered.' : step.status === 'error' ? 'Could not finish.'
      : !live ? 'No recorded final result.' : step.status === 'pending' ? 'Waiting to start.'
      : Object.hasOwn(STAGES, step.toolName) ? STAGES[step.toolName] : 'Working.';
    lines.push(`Clone ${index + 1}: ${status}`);
  });
  for (const name of draft?.conceptNames?.slice(-3) ?? []) {
    if (typeof name === 'string' && name.trim()) lines.push(`Drafted: ${name.replace(/[\r\n\t]/g, ' ').slice(0, 120)}`);
  }

  // Repeated QA attempts supersede prior outcomes for the same concept.
  const latest = new Map<number, NonNullable<typeof p>['qaResults'][number]>();
  for (const result of p?.qaResults ?? []) {
    const previous = latest.get(result.conceptIndex);
    if (!previous || result.attempt >= previous.attempt) latest.set(result.conceptIndex, result);
  }
  const qa = Array.from(latest.values()).map(result => result.status);
  const passed = qa.filter(status => status === 'passed').length;
  const revising = qa.filter(status => status === 'regenerating').length;
  const review = qa.filter(status => status === 'needs_review').length;
  if (qa.length) lines.push(`Quality checks: ${[passed ? `${passed} passed` : '', revising ? `${revising} ${live ? 'being revised' : 'marked for revision'}` : '', review ? `${review} need review` : ''].filter(Boolean).join('; ')}.`);
  else {
    const status = groups.find(group => group.qaStatus?.status === 'reviewing' || group.qaStatus?.status === 'regenerating')?.qaStatus?.status;
    if (status) lines.push(live ? (status === 'reviewing' ? 'Checking the current creative.' : 'Revising the current creative after quality checks.') : 'The last quality check has no recorded final result.');
    else if (p?.conceptProgress) lines.push(`Creative progress: ${count(p.conceptProgress.current)} of ${count(p.conceptProgress.total)}.`);
  }
  return lines;
}
